<template>
  <div id="payment-method-create">
    <SidebarLayout3 :breadcrumb="breadcrumb" :pageTitle="pageTitle">
      <!-- SIDEBAR -->
      <template #sidebar>
        <Heading element="h2" class="sidebar-title">
          Ações da página
        </Heading>
        <div class="sidebar-buttons">
          <Button
            :disabled="disableSaveButton"
            :tooltipText="
              'Para salvar, preencha os campos obrigatórios sinalizados com *'
            "
            class="sidebar-buttons--save"
            @click="paymentsId === null ? createPayment() : editPayment()"
          >
            Salvar
          </Button>
          <Button
            v-if="disableDeleteButton"
            variation="error"
            class="sidebar-buttons--delete"
            @click.stop.prevent="
              deleteModal(paymentId, form.exclusivoTarifa, form.nome)
            "
          >
            Excluir
          </Button>
          <Button
            layout="outline"
            class="sidebar-buttons--return"
            @click="
              $router.push({
                path: `/hoteis/formas-pagamento/${$route.params.id}`
              })
            "
          >
            Voltar sem salvar
          </Button>
        </div>
      </template>

      <!-- FORMULÁRIO -->
      <template #content>
        <FormSelect
          v-model="paymentMethod"
          :options="paymentOptions"
          required
          search
          label="Forma de pagamento"
          placeholder="Selecionar um tipo"
          class="text-input--email large"
        />

        <!-- SEGUNDA LINHA -->
        <span v-if="paymentMethod">
          <!-- INPUTS DE CARTÃO -->
          <div
            v-if="
              paymentMethod.text !== 'Pix' && paymentMethod.text !== 'Boleto'
            "
            class="form"
          >
            <FormInput
              v-model="form.numeroParcelas"
              :error="error.numeroParcelas"
              :showErrorFeedbackMessage="true"
              required
              type="number"
              tooltipMinWidth="225px"
              placeholder="Insira as parcelas"
              infoText="Informe o número de parcelas disponível para a bandeira informada. O parcelamento deve contemplar o valor sem juros, já que o Letsbook não acrescenta juros."
              label="Número de parcelas"
              class="text-input medium"
              infoIcon
            />

            <PmwFormCurrencyInput
              v-model="form.valorMinimoParcela"
              :error="error.valorMinimoParcela"
              :showErrorFeedbackMessage="true"
              :prefix="getCurrencySymbol"
              infoIcon
              tooltipMinWidth="165"
              placeholder="Insira o valor mínimo"
              infoText="Informe o valor mínimo de cada parcela permitido."
              label="Valor mínimo de parcelas"
              class="text-input--currency medium"
            />

            <InputCheckbox
              v-model="form.exclusivoTarifa"
              :disabled="disableExclusivaTarifa"
              :inputValue="'true'"
              :checked="form.exclusivoTarifa"
              infoIcon
              infoText="Quando o hotel já possuir uma forma de pagamento cadastrada, esta opção poderá ser utilizada para um meio de pagamento diferenciado por tarifa."
              tooltipMinWidth="225px"
              label="Exclusiva tarifa"
              class="text-input--radio large"
            />
          </div>

          <!-- INPUTS DE BOLETO -->
          <div v-if="paymentMethod.text === 'Boleto'" class="form">
            <FormSelect
              v-model="form.tipoDisponibilidade"
              :options="availabilityOptions"
              label="Disponibilidade"
              placeholder="Selecione uma disponibilidade"
              class="text-input large"
            />

            <FormInput
              v-model="form.minimoDiasAntecedencia"
              :error="error.minimoDiasAntecedencia"
              :showErrorFeedbackMessage="true"
              required
              infoIcon
              ref="minimoDiasAntecedencia"
              sufix="Dias"
              tooltipMinWidth="225px"
              infoText="Mínimo de antecedência para ativar a forma de pagamento, é necessário um mínimo de 3 dias para antecedência."
              label="Mínimo de antecedência"
              class="text-input medium"
            />

            <FormInput
              v-model="form.prazoDiasExpiracaoBoleto"
              :error="error.prazoDiasExpiracaoBoleto"
              :showErrorFeedbackMessage="true"
              required
              infoIcon
              ref="prazoDiasExpiracaoBoleto"
              sufix="Dias"
              tooltipMinWidth="225px"
              infoText="Quantidade de dias corridos em que a reserva será segurada para pagamento do boleto. Se não recebermos compensação do pagamento nesses dias, a reserva será expirada e o allotment liberado."
              label="Prazo de expiração"
              class="text-input medium"
            />

            <InputCheckbox
              v-model="form.exclusivoCentralReservas"
              :inputValue="'true'"
              :checked="form.exclusivoCentralReservas"
              infoIcon
              infoText="Quando o hotel já possuir uma forma de pagamento cadastrada, esta opção poderá ser utilizada para um meio de pagamento diferenciado por tarifa."
              tooltipMinWidth="225px"
              label="Exclusivo central"
              class="text-input--radio large"
            />
          </div>

          <!-- INPUTS DE PIX -->
          <div v-if="paymentMethod.text === 'Pix'" class="form">
            <FormSelect
              v-model="form.tipoDisponibilidade"
              :options="availabilityOptions"
              label="Disponibilidade"
              placeholder="Selecione uma disponibilidade"
              class="text-input large"
            />

            <FormInput
              :error="error.prazoMinutosExpiracaoPix"
              :showErrorFeedbackMessage="true"
              :disabled="gatewaypix === 'CIELO'"
              :value="
                gatewaypix === 'CIELO' ? 120 : form.prazoMinutosExpiracaoPix
              "
              required
              infoIcon
              ref="prazoMinutosExpiracaoPix"
              type="number"
              sufix="Min"
              tooltipMinWidth="225px"
              placeholder="Insira o tempo"
              infoText="Tempo em minutos que vamos segurar a disponibilidade da reserva e disponibilizar a transação para que o hóspede efetue o pagamento."
              label="Tempo de espera"
              class="text-input medium"
              @input="form.prazoMinutosExpiracaoPix = $event"
            />

            <InputCheckbox
              v-model="form.exclusivoCentralReservas"
              :inputValue="'true'"
              :checked="form.exclusivoCentralReservas"
              required
              infoIcon
              infoText="Quando o hotel já possuir uma forma de pagamento cadastrada, esta opção poderá ser utilizada para um meio de pagamento diferenciado por tarifa."
              tooltipMinWidth="225px"
              label="Exclusivo central"
              class="text-input--radio large"
            />
          </div>

          <div v-if="paymentMethod.text === 'Pix'">
            <PmwParagraph class="pix-text">
              Caso deseje fornecer desconto para esse meio de pagamento,
              preencha o campo abaixo com o valor em porcentagem.
            </PmwParagraph>
            <div class="pix-input">
              <FormInput
                v-model="form.descontoFormaPagamento"
                :error="error.descontoFormaPagamento"
                :showErrorFeedbackMessage="true"
                infoIcon
                sufix="%"
                type="number"
                tooltipMinWidth="222px"
                placeholder="00"
                infoText="Este desconto vai ser aplicado em todas as regras tarifárias (flutuações) em que estiver disponível o pix como forma de pagamento."
                label="Desconto"
                class="text-input normal"
              />

              <div
                v-if="
                  form.descontoFormaPagamento !== 0 &&
                    form.descontoFormaPagamento !== '' &&
                    form.descontoFormaPagamento !== null
                "
              >
                <PmwParagraph class="form--row-label">
                  Exibir o desconto Pix:
                </PmwParagraph>
                <div class="form--row-checklist">
                  <!-- Exibir desconto pix do começo -->
                  <PmwInputRadio
                    v-model="pixFromStart"
                    :inputValue="true"
                    :checked="pixFromStart"
                    label="Desde o início da jornada."
                    name="pixFromStart"
                  />

                  <!-- Exibir desconto pix apenas no checkout -->
                  <PmwInputRadio
                    v-model="pixFromStart"
                    :inputValue="false"
                    :checked="!pixFromStart"
                    label="Apenas no checkout"
                    name="pixFromStart"
                  />
                </div>
              </div>
            </div>
          </div>
        </span>

        <SeparateLine :left="0" :right="0" :top="40" :bottom="35" />

        <!-- FOOTER -->
        <span class="bottom-buttons">
          <Button
            layout="outline"
            class="bottom-buttons--return"
            @click="
              $router.push({
                path: `/hoteis/formas-pagamento/${$route.params.id}`
              })
            "
          >
            Voltar sem salvar
          </Button>
          <Button
            :disabled="disableSaveButton"
            :tooltipText="
              'Para salvar, preencha os campos obrigatórios sinalizados com *'
            "
            :popperStrategy="'auto'"
            class="bottom-buttons--save"
            @click="paymentsId === null ? createPayment() : editPayment()"
          >
            Salvar
          </Button>
        </span>
      </template>
    </SidebarLayout3>

    <!-- MODAL DELEÇÃO -->
    <PmwAlertModal
      v-model="showDeleteModal"
      closeButton
      title="Excluir forma de pagamento"
      icon="pmw-trash-can"
      iconColor="red-100"
      cancelButtonColor="grey-90"
      confirmButtonColor="red-100"
      @alert-modal-cancel="cancelDisable()"
      @alert-modal-confirm="deletePayment()"
    >
      <template #text>
        {{ disableMessage }} <br />
        Tem certeza que quer fazer isso?
      </template>
    </PmwAlertModal>
  </div>
</template>

<script>
/** Componentes da lib */
import PmwAlertModal from '@solucoes/components-lib-tmp/src/components/patterns/PmwAlertModal'
import Button from '@solucoes/components-lib-tmp/src/components/elements/Button'
import FormSelect from '@solucoes/components-lib-tmp/src/components/elements/FormSelect'
import Heading from '@solucoes/components-lib-tmp/src/components/elements/Heading'
import FormInput from '@solucoes/components-lib-tmp/src/components/elements/FormInput'
import PmwFormCurrencyInput from '@solucoes/components-lib-tmp/src/components/patterns/PmwFormCurrencyInput'
import SeparateLine from '@solucoes/components-lib-tmp/src/components/elements/SeparateLine'
import InputCheckbox from '@solucoes/components-lib-tmp/src/components/elements/InputCheckbox'
import PmwParagraph from '@solucoes/components-lib-tmp/src/components/elements/PmwParagraph'
import PmwInputRadio from '@solucoes/components-lib-tmp/src/components/elements/PmwInputRadio'

/** Imports do projeto */
import SidebarLayout3 from '@/layouts/SidebarLayout3'
import { mapGetters } from 'vuex'
import { capitalizeString } from '@/helpers/string'

export default {
  name: 'PaymentMethodsCreate',
  components: {
    Button,
    FormInput,
    FormSelect,
    Heading,
    SidebarLayout3,
    PmwAlertModal,
    SeparateLine,
    PmwFormCurrencyInput,
    InputCheckbox,
    PmwParagraph,
    PmwInputRadio
  },

  data() {
    return {
      gatewaypix: null /** Variável para guardar o gateway de pix do hotel */,
      pixFromStart: false /** Comportamento de exibição pix */,
      paymentMethod: null /** Forma de pagamento selecionada */,
      paymentOptions: [] /** Opções do select de forma de pagamento */,
      /** Opções do select de disponibilidade */
      availabilityOptions: [
        {
          text: 'Todos',
          value: 0
        },
        {
          text: 'Somente hospedagem',
          value: 1
        },
        {
          text: 'Somente experiências',
          value: 2
        }
      ],

      breadcrumb: [] /** Breadcrumb da página */,
      routeType: '' /** Guarda tipo de página (Edição/Criação)  */,
      pageTitle: null /** Título da página  */,
      showDeleteModal: false /** Exibição da modal de deleção */,
      disableExclusivaTarifa: false /** Exibição da modal de erro */,
      paymentDelete: null /** Guarda Id para deleção */,
      paymentId: null /** Guarda Id do pagamento */,
      allPayments: null /** Todos os itens cadastrados do hotel */,
      disableMessage: null /** Mensagem do modal de deleção */,

      error: {
        minimoDiasAntecedencia: false,
        prazoMinutosExpiracaoPix: false,
        numeroParcelas: false,
        prazoDiasExpiracaoBoleto: false,
        descontoFormaPagamento: false
      } /** Controle de erro */,
      /** V-model de cadastro/edição */
      form: {
        /** Comum */
        codigo: null,
        codigoHotel: null,
        nome: null,
        codigoTarifa: null,
        nomeHotel: null,

        /** Boleto/Pix */
        tipoDisponibilidade: {
          text: 'Todos',
          value: 0
        },
        prazoDiasExpiracaoBoleto: 3,
        prazoMinutosExpiracaoPix: 30,
        minimoDiasAntecedencia: 3,
        exclusivoCentralReservas: false,
        descontoFormaPagamento: 0,

        /** Cartão */
        exclusivoTarifa: false,
        numeroParcelas: 1,
        valorMinimoParcela: 0
      }
    }
  },

  computed: {
    /** Verificador para desabilitar/habilitar o botão de salvar */
    disableSaveButton() {
      if (
        /** Verifica campos caso seja modo de pagamento Pix */
        this.paymentMethod &&
        this.paymentMethod.text === 'Pix' &&
        this.form.prazoMinutosExpiracaoPix !== '' &&
        this.form.tipoDisponibilidade !== ''
      ) {
        return false
      } else if (
        /** Verifica campos caso seja modo de pagamento Boleto */
        this.paymentMethod &&
        this.paymentMethod.text === 'Boleto' &&
        this.form.prazoDiasExpiracaoBoleto !== '' &&
        this.form.minimoDiasAntecedencia !== '' &&
        this.form.tipoDisponibilidade !== ''
      ) {
        return false
      } else if (
        /** Verifica campos caso seja modo de pagamento cartão */
        this.paymentMethod &&
        this.paymentMethod.text !== 'Boleto' &&
        this.paymentMethod.text !== 'Pix' &&
        this.form.numeroParcelas !== null &&
        this.form.numeroParcelas !== ''
      ) {
        return false
      }

      return true
    },

    /** Verificador para desabilitar/habilitar o botão de desabilitar */
    disableDeleteButton() {
      if (this.$route.params.paymentId) {
        return true
      }
      return false
    },

    ...mapGetters('hotel', ['getCurrencySymbol'])
  },

  watch: {
    'paymentMethod.text': {
      handler: function(newValue, oldValue) {
        /** Filtro para saber se já existe uma forma de pagamento desse tipo cadastrada */
        const results = this.allPayments.filter((data) => {
          return (
            data.nome
              .toLowerCase()
              .trim()
              .replace(/ +/g, '')
              .indexOf(
                this.paymentMethod.text
                  .toLowerCase()
                  .trim()
                  .replace(/ +/g, '')
              ) > -1
          )
        })

        /** Verificação para bloquear o inputCheckbox de exclusiva tarifa */
        if (
          (results.length === 0 && newValue !== oldValue) ||
          this.$route.params.paymentId === results[0]
        ) {
          this.disableExclusivaTarifa = true
        } else if (this.$route.params.paymentId && results.length <= 1) {
          this.disableExclusivaTarifa = true
        } else {
          this.disableExclusivaTarifa = false
        }

        /** Limpa o formulário ao selecionar outra forma de pagamento */
        if (newValue !== oldValue && oldValue !== undefined) {
          /** Define os valores padrão */
          this.form = {
            /** Comum */
            codigo: null,
            codigoHotel: null,
            nome: null,
            codigoTarifa: null,
            nomeHotel: null,

            /** Boleto/Pix */
            tipoDisponibilidade: {
              text: 'Todos',
              value: 0
            },
            prazoDiasExpiracaoBoleto: 3,
            prazoMinutosExpiracaoPix: 30,
            minimoDiasAntecedencia: 3,
            exclusivoCentralReservas: false,
            descontoFormaPagamento: 0,

            /** Cartão */
            exclusivoTarifa: false,
            numeroParcelas: 1,
            valorMinimoParcela: 0
          }
        }
      }
    },

    /** Verifica o novo valor de dias de antecedencia para remover o erro do Boleto */
    'form.minimoDiasAntecedencia': {
      handler: function(newValue, oldValue) {
        if (
          this.error.minimoDiasAntecedencia === true &&
          newValue !== oldValue
        ) {
          this.error.minimoDiasAntecedencia = false
        }
      }
    },

    /** Verifica o novo valor de dias de prazo de expiração de boleto para remover o erro  */
    'form.prazoDiasExpiracaoBoleto': {
      handler: function(newValue, oldValue) {
        if (
          this.error.prazoDiasExpiracaoBoleto === true &&
          newValue !== oldValue
        ) {
          this.error.prazoDiasExpiracaoBoleto = false
        }
      }
    },

    /** Verifica o novo valor minutos de expiração para remover o erro do Pix */
    'form.prazoMinutosExpiracaoPix': {
      handler: function(newValue, oldValue) {
        if (
          this.error.prazoMinutosExpiracaoPix === true &&
          newValue !== oldValue
        ) {
          this.error.prazoMinutosExpiracaoPix = false
        }
      }
    },

    /** Verifica o novo valor de número de parcelas para remover o erro do campo */
    'form.numeroParcelas': {
      handler: function(newValue, oldValue) {
        if (this.error.numeroParcelas === true && newValue !== oldValue) {
          this.error.numeroParcelas = false
        }
      }
    },

    /** Verifica o novo valor de desconto Pix para remover o erro do campo */
    'form.descontoFormaPagamento': {
      handler: function(newValue, oldValue) {
        if (
          this.error.descontoFormaPagamento === true &&
          newValue !== oldValue
        ) {
          this.error.descontoFormaPagamento = false
        }
      }
    },

    'form.valorMinimoParcela': {
      handler: function(newValue) {
        if (newValue === '') {
          this.form.valorMinimoParcela = 0
        }
      }
    }
  },

  async beforeCreate() {
    /** Faz a chamada de todos os pagamento para ser usado no watch */
    await this.$paymentMethods
      .listPaymentMethods(this.$route.params.id)
      .then((response) => {
        const results = response.data
        results.map(function(key, index) {
          results[index]['data'] = {
            nome: key.nome,
            numeroParcelas: key.numeroParcelas,
            valorMinimoParcela: key.valorMinimoParcela,
            exclusivoTarifa: key.exclusivoTarifa,
            exclusivoCentralReservas: key.exclusivoCentralReservas,
            descontoFormaPagamento: key.descontoFormaPagamento,
            id: key.codigo,
            codigo: key.codigo
          }
        })
        this.allPayments = results
      })
  },

  async created() {
    /** Requisição de dados de hoteis */
    await this.$hotel.get(this.$route.params.id).then((response) => {
      this.hotel = response.data
      this.gatewaypix = response.data.gatewayPix
      this.breadcrumb = [
        {
          label: 'Letsbook'
        },
        {
          label: 'Hotéis',
          url: '/hoteis',
          isSpa: true /** se deve ser um link para o site novo ou antigo */
        },
        {
          label: capitalizeString(this.hotel.nome),
          url: `/hoteis/editar/${this.hotel.codigo}`,
          isSpa: true /** se deve ser um link para o site novo ou antigo */
        },
        {
          label: 'Formas de pagamento',
          url: `/hoteis/formas-pagamento/${this.hotel.codigo}`,
          isSpa: true /** se deve ser um link para o site novo ou antigo */
        },
        {
          label: 'Criar forma de pagamento',
          url: '',
          isSpa: true /** se deve ser um link para o site novo ou antigo */
        }
      ]
    })

    /** Verifica se é uma página de edição ou criação */
    this.pageType()

    /** Chamada dos itens do select de forma de pagamento */
    await this.$paymentMethods
      .getPaymentMethods(this.$route.params.id)
      .then((response) => {
        const results = response.data
        results.map(function(key, index) {
          results[index] = {
            text: key.nome,
            chave: key.chave
          }
        })
        this.paymentOptions = results
      })

    /** Se for edição, pegar o ID so pagamento */
    this.paymentsId = this.$route.params.paymentId || null
  },

  methods: {
    /** Verifica se é edição ou criação */
    pageType() {
      if (!this.$route.params.paymentId) {
        this.routeType = true
        this.pageTitle = 'Criar forma de pagamento'
      } else {
        this.routeType = false
        this.getSinglePayment()
      }
    },

    /** Retorna dados do pagamento */
    async getSinglePayment() {
      await this.$paymentMethods
        .getSinglePayment(this.$route.params.paymentId)
        .then((response) => {
          const results = response.data

          this.breadcrumb = [
            {
              label: 'Letsbook'
            },
            {
              label: 'Hotéis',
              url: '/hoteis',
              isSpa: true /** se deve ser um link para o site novo ou antigo */
            },
            {
              label: this.hotel.nome,
              url: `/hoteis/editar/${this.hotel.codigo}`,
              isSpa: true /** se deve ser um link para o site novo ou antigo */
            },
            {
              label: 'Formas de pagamento',
              url: `/hoteis/formas-pagamento/${this.hotel.codigo}`,
              isSpa: true /** se deve ser um link para o site novo ou antigo */
            },
            {
              label: `Editando ${results.nome}`,
              url: '',
              isSpa: true /** se deve ser um link para o site novo ou antigo */
            }
          ]

          this.pageTitle = 'Editando ' + results.nome

          /** Define o valor dos v-models */
          this.form = results

          /** Define o valor do v-model do select de disponibilidade */
          if (this.form.tipoDisponibilidade === 0) {
            this.form.tipoDisponibilidade = this.availabilityOptions[0]
          } else if (this.form.tipoDisponibilidade === 1) {
            this.form.tipoDisponibilidade = this.availabilityOptions[1]
          } else {
            this.form.tipoDisponibilidade = this.availabilityOptions[2]
          }

          const selectedPayment = {
            text: results.nome,
            value: results.codigo,
            id: results.codigo
          }
          this.paymentMethod = selectedPayment
          this.pixFromStart = results.apresentaDescontoDesdeInicio
        })
    },

    /** Cria um novo pagamento */
    createPayment() {
      /** Verifica o valor minimo de parcelas, caso seja vazio, coloca o valor 0 */
      if (this.valorMinimoParcela === null) {
        this.valorMinimoParcela = 0
      }

      /** Verifica o minimo de dias de antecedência em boleto */
      if (
        this.paymentMethod.text === 'Boleto' &&
        this.form.minimoDiasAntecedencia < 3
      ) {
        this.error.minimoDiasAntecedencia = true
        return this.$bus.$emit('show-alert-chip', {
          message: 'É necessário um mínimo de 3 dias para antecedência.'
        })
      }

      /** Verifica o minimo de dias de antecedência em boleto */
      if (
        this.paymentMethod.text === 'Boleto' &&
        this.form.prazoDiasExpiracaoBoleto < 3
      ) {
        this.error.prazoDiasExpiracaoBoleto = true
        return this.$bus.$emit('show-alert-chip', {
          message:
            'É necessário um mínimo de 3 dias para o prazo de expiração de boleto.'
        })
      }

      /** Verifica o valor de tempo de espera do pagamento via Pix */
      if (
        this.paymentMethod.text === 'Pix' &&
        this.form.prazoMinutosExpiracaoPix < 30
      ) {
        this.error.prazoMinutosExpiracaoPix = true
        return this.$bus.$emit('show-alert-chip', {
          message: 'É necessário um mínimo de 30 minutos de espera.'
        })
      }

      /** Verifica o valor máximo de tempo de espera do pagamento via Pix para Redecard */
      if (
        this.paymentMethod.text === 'Pix' &&
        this.gatewaypix === 'REDECARD' &&
        this.form.prazoMinutosExpiracaoPix > 360
      ) {
        this.error.prazoMinutosExpiracaoPix = true
        return this.$bus.$emit('show-alert-chip', {
          message: 'É necessário um máximo de 360 minutos de espera.'
        })
      }

      /** Verifica se o desconto Pix está entre 0 e 100% */
      if (
        this.paymentMethod.text === 'Pix' &&
        (this.form.descontoFormaPagamento < 0 ||
          this.form.descontoFormaPagamento > 100)
      ) {
        this.error.descontoFormaPagamento = true
        return this.$bus.$emit('show-alert-chip', {
          message: 'O desconto deve ser entre 0% e 100%.'
        })
      }

      /** Atribui a disponibilidade o valor do select */
      let disponibilidade = null
      if (this.form.tipoDisponibilidade) {
        disponibilidade = this.form.tipoDisponibilidade.value
      } else {
        disponibilidade = null
      }

      if (
        this.paymentMethod.text !== 'Pix' &&
        this.paymentMethod.text !== 'Boleto' &&
        +this.form.numeroParcelas < 1
      ) {
        this.error.numeroParcelas = true
        return this.$bus.$emit('show-alert-chip', {
          message: 'É necessário um número de parcelas maior que 0.'
        })
      }

      this.$paymentMethods
        .createPaymentMethod({
          codigoHotel: parseInt(this.$route.params.id),
          nome: this.paymentMethod.text,
          numeroParcelas: parseInt(this.form.numeroParcelas),
          valorMinimoParcela: this.form.valorMinimoParcela,
          exclusivoTarifa: this.form.exclusivoTarifa,
          exclusivoCentralReservas: this.form.exclusivoCentralReservas,
          descontoFormaPagamento: this.form.descontoFormaPagamento,
          tipoDisponibilidade: disponibilidade,
          minimoDiasAntecedencia: this.form.minimoDiasAntecedencia,
          prazoMinutosExpiracaoPix: parseInt(
            this.form.prazoMinutosExpiracaoPix
          ),
          prazoDiasExpiracaoBoleto: parseInt(
            this.form.prazoDiasExpiracaoBoleto
          ),
          apresentaDescontoDesdeInicio: this.pixFromStart
        })
        .then(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Forma de pagamento criada com sucesso.'
          })
          this.$router.push({
            path: `/hoteis/formas-pagamento/${this.$route.params.id}`
          })
        })
        .catch((err) => {
          this.$bus.$emit('show-alert-chip', {
            message: err.messages[0]
          })
        })
    },

    /** Edição de pagamento */
    editPayment() {
      /** Verifica o valor minimo de parcelas, caso seja vazio, coloca o valor 0 */
      if (this.valorMinimoParcela === null) {
        this.valorMinimoParcela = 0
      }

      /** Verifica o minimo de dias de antecedência em boleto */
      if (
        this.paymentMethod.text === 'Boleto' &&
        this.form.minimoDiasAntecedencia < 3
      ) {
        this.error.minimoDiasAntecedencia = true
        return this.$bus.$emit('show-alert-chip', {
          message: 'É necessário um mínimo de 3 dias para antecedência.'
        })
      }

      /** Verifica o minimo de dias de antecedência em boleto */
      if (
        this.paymentMethod.text === 'Boleto' &&
        this.form.prazoDiasExpiracaoBoleto < 3
      ) {
        this.error.prazoDiasExpiracaoBoleto = true
        return this.$bus.$emit('show-alert-chip', {
          message:
            'É necessário um mínimo de 3 dias para o prazo de expiração de boleto.'
        })
      }

      /** Verifica o valor de tempo de espera do pagamento via Pix */
      if (
        this.paymentMethod.text === 'Pix' &&
        this.form.prazoMinutosExpiracaoPix < 30
      ) {
        this.error.prazoMinutosExpiracaoPix = true
        return this.$bus.$emit('show-alert-chip', {
          message: 'É necessário um mínimo de 30 minutos de espera.'
        })
      }

      /** Verifica o valor máximo de tempo de espera do pagamento via Pix para Redecard */
      if (
        this.paymentMethod.text === 'Pix' &&
        this.gatewaypix === 'REDECARD' &&
        this.form.prazoMinutosExpiracaoPix > 360
      ) {
        this.error.prazoMinutosExpiracaoPix = true
        return this.$bus.$emit('show-alert-chip', {
          message: 'É necessário um máximo de 360 minutos de espera.'
        })
      }

      /** Verifica se o desconto Pix está entre 0 e 100% */
      if (
        this.paymentMethod.text === 'Pix' &&
        (this.form.descontoFormaPagamento < 0 ||
          this.form.descontoFormaPagamento > 100)
      ) {
        this.error.descontoFormaPagamento = true
        return this.$bus.$emit('show-alert-chip', {
          message: 'O desconto deve ser entre 0% e 100%.'
        })
      }

      /** Atribui a disponibilidade o valor do select */
      let disponibilidade = null
      if (this.form.tipoDisponibilidade) {
        disponibilidade = this.form.tipoDisponibilidade.value
      } else {
        disponibilidade = null
      }

      if (
        this.paymentMethod.text !== 'Pix' &&
        this.paymentMethod.text !== 'Boleto' &&
        +this.form.numeroParcelas < 1
      ) {
        this.error.numeroParcelas = true
        return this.$bus.$emit('show-alert-chip', {
          message: 'É necessário um número de parcelas maior que 0.'
        })
      }

      this.$paymentMethods
        .editPaymentMethod({
          codigo: parseInt(this.$route.params.paymentId),
          nome: this.paymentMethod.text,
          numeroParcelas: parseInt(this.form.numeroParcelas),
          valorMinimoParcela: this.form.valorMinimoParcela,
          exclusivoTarifa: this.form.exclusivoTarifa,
          exclusivoCentralReservas: this.form.exclusivoCentralReservas,
          descontoFormaPagamento: this.form.descontoFormaPagamento,
          tipoDisponibilidade: disponibilidade,
          minimoDiasAntecedencia: this.form.minimoDiasAntecedencia,
          prazoMinutosExpiracaoPix: parseInt(
            this.form.prazoMinutosExpiracaoPix
          ),
          prazoDiasExpiracaoBoleto: parseInt(
            this.form.prazoDiasExpiracaoBoleto
          ),
          apresentaDescontoDesdeInicio: this.pixFromStart
        })
        .then(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Forma de pagamento editada com sucesso.'
          })
          this.$router.push({
            path: `/hoteis/formas-pagamento/${this.$route.params.id}`
          })
        })
        .catch((err) => {
          this.$bus.$emit('show-alert-chip', {
            message: err.messages[0]
          })
        })
    },

    /** Desabilitar forma de pagamento */
    deletePayment() {
      this.$paymentMethods
        .disablePaymentMethods(
          { codigos: [this.$route.params.paymentId] },
          parseInt(this.$route.params.id)
        )
        .then(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Forma de pagamento excluída com sucesso'
          })
        })
      this.$router
        .push({
          path: `/hoteis/formas-pagamento/${this.$route.params.id}`
        })
        .catch(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Erro ao excluir forma de pagamento'
          })
        })
    },

    /** verificar se dado existe */
    ifExists(element) {
      /** se for array */
      let result = null
      if (element && typeof element.value !== 'undefined') {
        result = element.value
        /** se for string */
      } else if (element !== '') {
        result = element
      }
      return result === true ? 1 : result
    },

    /** Controle de deleção */
    deleteModal(paymentId, exclusivoTarifa, paymentName) {
      this.disableRule(paymentId, exclusivoTarifa, paymentName)
      this.paymentDelete = paymentId
    },

    /** Modifica a mensagem do modal de deleção de acordo com os casos */
    disableRule(paymentId, exclusivoTarifa, paymentName) {
      /** Verifica se o item é único ou não */
      const allItems = this.allPayments.filter((data) => {
        return (
          data.nome
            .toLowerCase()
            .trim()
            .replace(/ +/g, '')
            .indexOf(
              paymentName
                .toLowerCase()
                .trim()
                .replace(/ +/g, '')
            ) > -1
        )
      })

      if (exclusivoTarifa === true) {
        /** Caso não seja exclusivo do hotel */
        this.disableMessage =
          'Ao excluir uma forma de pagamento, ela não estará mais disponível para uso, essa é uma ação irreversível.'
        this.showDeleteModal = true
      } else if (exclusivoTarifa === false && allItems.length > 1) {
        /** Caso seja exclusivo do hotel e tenha outro item para ser o novo exclusivo */
        this.disableMessage =
          'Esta é uma forma de pagamento padrão do hotel, ao excluí-la, uma nova forma deste tipo vai se tornar padrão.'
        this.showDeleteModal = true
      } else {
        /** Outros casos */
        this.disableMessage =
          'Ao excluir uma forma de pagamento, ela não estará mais disponível para uso, essa é uma ação irreversível.'
        this.showDeleteModal = true
      }
    },

    /** Controle de cancelamento de deleção */
    cancelDisable() {
      this.showDeleteModal = false
      this.paymentDelete = null
    }
  }
}
</script>

<style lang="scss" scoped>
.large {
  width: 270px;
}
.medium {
  width: 200px;
}
.small {
  width: 56px;
}
.normal {
  width: 125px;
}
.sidebar-title {
  margin-top: 40px;
  margin-left: 20px;
  margin-bottom: 15px;
}
.sidebar-buttons {
  width: 215px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: auto;
}

.pix-text {
  width: 442px;
  line-height: 21px;
  margin-bottom: 32px;
  margin-top: 40px;
}

.text-input {
  &--radio {
    margin-top: 40px;
    &__info {
      margin-top: 40px;
      margin-left: -15px;
    }
  }
  &--email {
    margin-bottom: 30px;
    margin-top: 37px;
  }
  &--limit {
    width: 141px;
  }
  &--currency {
    bottom: 5px;

    ::v-deep {
      .pmw-form-field--label {
        font-weight: font-weight(weight-medium);
      }

      .pmw-currency-input--clear {
        margin-top: 5px;
        margin-left: 1px;
      }

      .pmw-currency-input--field {
        height: 35px;
      }

      .pmw-form-field--asterisk {
        margin-left: 6px;
      }
    }
  }
}

.bottom-buttons {
  display: flex;
  justify-content: space-between;
  &--return,
  &--save {
    width: 215px;
  }
}

::v-deep .content--white_card {
  padding: {
    top: 10px;
  }
}
::v-deep .lb-breadcrumb--link:nth-child(2) {
  cursor: default;
}
::v-deep .pmw-custom-tooltip {
  line-height: 18px;
}

.form {
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  &--row-checklist {
    display: flex;
    flex-direction: row;
    gap: 15px;
  }

  &--row-label {
    font-size: font-size('size-medium');
    font-weight: font-weight('weight-medium');
    margin-bottom: 20px;
  }
}

.pix-input {
  display: flex;
  gap: pmw-space(5);
}
</style>
